import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { prosemirrorClientId } from "../../internal/prosemirror/client-id";
import { $editorState } from "../../internal/prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../../internal/prosemirror/editorStateKey";
import { $presenceConnectionEffect } from "../../internal/prosemirror/presence-connection.atom";
import {
	$presenceIndicators,
	$presentUsers,
	$stepMaps,
	type PresentUser,
} from "../../internal/prosemirror/presence.atom";
import {
	type MappedPresenceIndicator,
	useMappedPresenceIndicators,
} from "./useMappedPresenceIndicators";

// Re-export for convenience
export type { MappedPresenceIndicator, PresentUser };

/**
 * Announce presence to other users in a specific page, and receive presence updates from other
 * users.
 */
export function usePresence({ documentId, pageId }: { documentId: string; pageId: string }) {
	const key = useMemo(
		() => editorStateKeyForDocsRoute({ documentId, pageId }),
		[documentId, pageId]
	);

	// Subscribe to the presence connection effect. This opens a single connect() call when the
	// first component subscribes and closes it when the last unsubscribes.
	useAtomValue($presenceConnectionEffect(key));

	// Read presence state
	const rawIndicators = useAtomValue($presenceIndicators(key));
	const stepMaps = useAtomValue($stepMaps(key));
	const editorState = useAtomValue($editorState(key));
	const presentUsers = useAtomValue($presentUsers(key));

	// Rebase indicators to current document version
	const mappedIndicators = useMappedPresenceIndicators(
		editorState ?? undefined,
		rawIndicators,
		stepMaps
	);

	return {
		/** Presence indicators rebased to current document version */
		indicators: mappedIndicators,
		/** The local client ID */
		localClientId: prosemirrorClientId,
		/** List of present users, sorted by userID */
		presentUsers,
	};
}
